/**
 * Page changements détectés — /discovery/changes
 * GET /api/discovery/changes/ (PENDING par défaut)
 * Affiche : nom événement, type changement, avant/après, boutons update/ignore.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Alert, CircularProgress } from '@mui/material'
import './Discovery.css'
import { getEventChanges, resolveEventChange } from '../../utils/discoveryApi'

const Changes = () => {
  const { t } = useTranslation()
  const [changes, setChanges] = useState([])
  const [loading, setLoading] = useState(true)
  const [resolvingId, setResolvingId] = useState(null)

  useEffect(() => {
    fetchChanges()
  }, [])

  const fetchChanges = async () => {
    try {
      setLoading(true)
      const res = await getEventChanges()
      if (res?.status === 200 && res.data) {
        setChanges(res.data.results || [])
      }
    } catch (err) {
      console.error('Failed to load changes:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleResolve = async (changeId, action) => {
    setResolvingId(changeId)
    try {
      await resolveEventChange(changeId, action)
      setChanges((prev) => prev.filter((c) => c.id !== changeId))
    } catch (err) {
      console.error('Failed to resolve change:', err)
    } finally {
      setResolvingId(null)
    }
  }

  if (loading) {
    return (
      <Box className="dsc-page">
        <CircularProgress />
      </Box>
    )
  }

  const changeTypeLabels = {
    TIME_CHANGED: t('discovery.timeChanged'),
    VENUE_CHANGED: t('discovery.venueChanged'),
    CANCELLED: t('discovery.cancelled'),
    POSTPONED: t('discovery.postponed'),
  }

  if (changes.length === 0) {
    return (
      <Box className="dsc-page">
        <Box className="dsc-empty">
          <h2>{t('discovery.noChanges')}</h2>
          <p>{t('discovery.noChangesDesc')}</p>
        </Box>
      </Box>
    )
  }

  return (
    <Box className="dsc-page dsc-changes">
      <Box className="dsc-header">
        <h1>{t('discovery.changes')}</h1>
      </Box>

      <Alert severity="info">{t('discovery.changesInfo')}</Alert>

      <Box className="dsc-changes-list">
        {changes.map((change) => {
          const details = change.details || {}
          const typeLabel = changeTypeLabels[change.change_type] || change.change_type
          const before = details.before || {}
          const after = details.after || {}

          return (
            <Box key={change.id} className="dsc-change-item">
              <Box className="dsc-change-header">
                <h3>{change.event_name}</h3>
                <span className="dsc-change-type">{typeLabel}</span>
              </Box>

              {change.change_type === 'TIME_CHANGED' && (
                <Box className="dsc-change-details">
                  <p>
                    <strong>{t('discovery.before')}:</strong> {before.start_datetime || '-'}
                  </p>
                  <p>
                    <strong>{t('discovery.after')}:</strong> {after.start_datetime || '-'}
                  </p>
                </Box>
              )}

              {change.change_type === 'VENUE_CHANGED' && (
                <Box className="dsc-change-details">
                  <p>
                    <strong>{t('discovery.before')}:</strong> {before.place || '-'}
                  </p>
                  <p>
                    <strong>{t('discovery.after')}:</strong> {after.place || '-'}
                  </p>
                </Box>
              )}

              {(change.change_type === 'CANCELLED' || change.change_type === 'POSTPONED') && (
                <Box className="dsc-change-details">
                  <p>{t('discovery.statusChanged')}</p>
                </Box>
              )}

              <Box className="dsc-change-actions">
                <Button
                  onClick={() => handleResolve(change.id, 'update')}
                  disabled={resolvingId === change.id}
                  className="dsc-action-btn dsc-action-update"
                >
                  {t('discovery.updateCalendar')}
                </Button>
                <Button
                  onClick={() => handleResolve(change.id, 'ignore')}
                  disabled={resolvingId === change.id}
                  className="dsc-action-btn dsc-action-ignore"
                >
                  {t('discovery.ignore')}
                </Button>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default Changes
